// apps/web/src/hooks/use-messages.ts
import useSWRInfinite from 'swr/infinite'
import { useCallback } from 'react'
import { api } from '../lib/api-client'
import type { Message, CursorPage } from '@viecxanh/chat-shared'
import { MSG_PAGE_SIZE } from '../lib/constants'

export function useMessages(conversationId: string | null) {
  const getKey = (pageIndex: number, previousPage: CursorPage<Message> | null) => {
    if (!conversationId) return null
    if (pageIndex > 0 && !previousPage?.next_cursor) return null
    const params = new URLSearchParams({ limit: String(MSG_PAGE_SIZE) })
    if (pageIndex > 0 && previousPage?.next_cursor) {
      params.set('before_cursor', previousPage.next_cursor)
    }
    return `/api/admin/conversations/${conversationId}/messages?${params.toString()}`
  }

  const { data, error, isLoading, size, setSize, mutate } = useSWRInfinite<CursorPage<Message>>(
    getKey,
    (url: string) => api.get<CursorPage<Message>>(url),
    { revalidateFirstPage: false },
  )

  // Flatten pages (newest first → displayed bottom up)
  const messages = data ? data.flatMap((p) => p.items).reverse() : []
  const hasMore = data ? Boolean(data[data.length - 1]?.next_cursor) : false

  const loadMore = useCallback(() => {
    void setSize((s) => s + 1)
  }, [setSize])

  const sendMessage = useCallback(
    async (payload: { content: string; type?: string; attachments?: unknown[]; reply_to?: string }) => {
      if (!conversationId) return
      const tempMsg: Message = {
        _id: `temp-${Date.now()}`,
        conversation_id: conversationId,
        content: payload.content,
        type: (payload.type as Message['type']) ?? 'text',
        attachments: (payload.attachments as Message['attachments']) ?? [],
        reply_to: null,
        sender: { user_type: 'staff', user_id: 0, name: 'Bạn' },
        status: 'sent',
        sent_at: new Date().toISOString(),
        edited_at: null,
        deleted_at: null,
      }

      await mutate(
        async (pages) => {
          await api.post(`/api/admin/conversations/${conversationId}/messages`, payload)
          return pages
        },
        {
          optimisticData: (pages) => {
            if (!pages) return []
            const first = pages[0]
            return [{ ...first, items: [tempMsg, ...first.items] }, ...pages.slice(1)]
          },
          rollbackOnError: true,
        },
      )
    },
    [conversationId, mutate],
  )

  return { messages, hasMore, isLoading, error, loadMore, sendMessage, mutate, size }
}
